import React, {useCallback, useRef, useState} from "react";
import {useSiren} from "../SirenContext";
import {useGetContactGroups} from "../hooks/useGetContactGroups";
import {Contact, ContactGroup} from "../typedefs";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useNavigate} from "react-router-dom";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {PaginatedSelector, HeaderComponentProps} from "../../../components/paginated-selector/paginated-selector";
import {useCopyID} from "../../../lib/misc/useCopyID";
import {useArchiveToggleContactGroup} from "../hooks/useArchiveToggleContactGroup";
import {useDeleteModal} from "../hooks/useDeleteModal";
import {platformAPI} from "../../../api/axiosConfig";
import {isNilObjectID} from "../../../lib/objectid/objectid";
import GroupsIcon from "@mui/icons-material/Groups";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PersonIcon from "@mui/icons-material/Person";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import {ContactGroupTypeFilter} from "../components/ContactGroupTypeFilter";
import {useGetContactGroupContacts} from "../hooks/useGetContactGroupContacts";
import ModalV2 from "../../../components/portal/ModalV2";

type ContactGroupListProps = {
    archived?: boolean;
}

export function ContactGroupList({archived = false}: ContactGroupListProps) {
    const {clientID} = useSiren();
    const {isSuperAdmin} = useAuth();
    const {navigateWithClientID} = useNavigateWithClientID();
    const getItems = useGetContactGroups(archived);
    const refetchRef = useRef<(() => void) | null>(null);
    const [membersGroup, setMembersGroup] = useState<ContactGroup | null>(null);

    const getColumns = useCallback((item: ContactGroup) => {
        const isDepartmentLinked = !isNilObjectID(item.departmentID);
        const count = item.contactIDs?.length ?? 0;
        return [
            isDepartmentLinked
                ? <ApartmentIcon fontSize="small" titleAccess="Department-linked"/>
                : <GroupsIcon fontSize="small" titleAccess="Manual"/>,
            item.name,
            <button
                className="px-2 py-0.5 rounded-full text-sm font-medium bg-desaturated-grey text-grey hover:bg-light-grey hover:text-white transition-colors"
                onClick={(e) => { e.stopPropagation(); setMembersGroup(item); }}
            >
                {count} members
            </button>,
        ];
    }, []);

    const renderHeader = useCallback((props: HeaderComponentProps) => {
        if (props.columnIndex === 0) {
            return <ContactGroupTypeFilter {...props}/>;
        }
        return <span className="font-bold">{props.label}</span>;
    }, []);

    const renderActions = useCallback((item: ContactGroup) => (
        <ContactGroupActions item={item} refetch={() => refetchRef.current?.()} archived={archived} onViewMembers={() => setMembersGroup(item)}/>
    ), [archived]);

    if (!clientID && isSuperAdmin) return null;

    return (
        <>
            <PaginatedSelector
                key={clientID}
                headers={["Type", "Name", "Contacts"]}
                columnTemplate="140px 300px minmax(0,1fr)"
                getItems={getItems}
                getColumns={getColumns}
                disableSelect={true}
                renderActions={renderActions}
                renderHeader={renderHeader}
                nonClickableColumns={[0, 2]}
                onColumnClick={(item) => navigateWithClientID(`siren/groups/${item.id}/edit?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                syncWithUrl={true}
                onRefetchReady={(refetch) => { refetchRef.current = refetch; }}
            />
            {membersGroup && (
                <ContactGroupMembersModal group={membersGroup} onClose={() => setMembersGroup(null)}/>
            )}
        </>
    );
}

function ContactGroupActions({item, refetch, archived, onViewMembers}: { item: ContactGroup; refetch: () => void; archived: boolean; onViewMembers: () => void }) {
    const {navigateWithClientID} = useNavigateWithClientID();
    const navigate = useNavigate();
    const {copyID, isCopied} = useCopyID();
    const {submit: toggleArchive} = useArchiveToggleContactGroup(item.id, refetch);
    const {DeleteModal, openDeleteModal} = useDeleteModal(
        "Contact Group",
        item.name,
        `api/contact-groups/${item.id}`,
        refetch,
        platformAPI,
    );
    const isDepartmentLinked = !isNilObjectID(item.departmentID);

    return (
        <>
            <DeleteModal/>
            <ActionSelect label="Actions">
                {!archived && !isDepartmentLinked && (
                    <Action label="Edit" onSelect={() => navigateWithClientID(`siren/groups/${item.id}/edit?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}/>
                )}
                {!archived && isDepartmentLinked && (
                    <Action label="Edit Department" onSelect={() => navigate(`/management/departments/edit/${item.departmentID}`)}/>
                )}
                <Action label="View Members" onSelect={onViewMembers}/>
                <Action label={isCopied(item.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(item.id)}/>
                {!isDepartmentLinked && (
                    <Action label={archived ? "Restore" : "Archive"} onSelect={toggleArchive}/>
                )}
                {archived && !isDepartmentLinked && (
                    <Action label="Delete" onSelect={openDeleteModal}/>
                )}
            </ActionSelect>
        </>
    );
}

function ContactGroupMembersModal({group, onClose}: { group: ContactGroup; onClose: () => void }) {
    const getContacts = useGetContactGroupContacts(group.id);
    const {navigateWithClientIDPath} = useNavigateWithClientID();

    const getColumns = useCallback((contact: Contact) => {
        const isUserLinked = !isNilObjectID(contact.userID);
        return [
            isUserLinked ? <PersonIcon fontSize="small" titleAccess="User-linked"/> : <AlternateEmailIcon fontSize="small" titleAccess="Manual"/>,
            `${contact.firstName} ${contact.lastName}`,
            contact.phoneMobileWork?.e164 ?? "",
            contact.phoneMobilePersonal?.e164 ?? "",
            contact.emailWork,
        ];
    }, []);

    return (
        <ModalV2 open={true} setOpen={(open: boolean) => { if (!open) onClose(); }}>
            <h2 className="text-lg font-semibold mb-4">{group.name} — Members ({group.contactIDs?.length ?? 0})</h2>
            <PaginatedSelector
                headers={["Type", "Name", "Work Mobile", "Personal Mobile", "Email"]}
                columnTemplate="80px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)"
                getItems={getContacts}
                getColumns={getColumns}
                nonClickableColumns={[0]}
                disableSelect={true}
                onColumnClick={(contact) => window.open(navigateWithClientIDPath(`siren/contacts/${contact.id}/edit`), "_blank")}
            />
        </ModalV2>
    );
}
